import { updateTooltips } from '@solar-assistant/shared-ui/js/elements/tooltips.js'

let Hooks = {}

Hooks.HelpMenu = {
    mounted() {
        window.addEventListener("phx:page-loading-stop", event => this.populatePageSelector());
        this.populatePageSelector();
    },
    destroyed() {
        // remove global listener when navigating away
        window.removeEventListener("phx:page-loading-stop", this._onPageStop)
    },
    populatePageSelector(event) {
        const pageIndex = window.document.getElementById("page-index");
        const pageSelector = document.getElementById("page-selector");
        if (!pageIndex || !pageSelector) return;

        while (pageSelector.firstChild) {
            pageSelector.removeChild(pageSelector.firstChild);
        }
        pageIndex.querySelectorAll('.section').forEach(section => {
            const group = document.createElement("optgroup");
            group.setAttribute("label", section.textContent);

            section.nextElementSibling.querySelectorAll('.item').forEach(item => {
                const option = document.createElement("option");
                option.value = item.getAttribute("href");
                if (window.top.location.pathname === item.getAttribute("href")) {
                    option.selected = true
                }
                option.appendChild(document.createTextNode(item.textContent))
                group.appendChild(option);
            })

            pageSelector.appendChild(group);
        });
    }
}


Hooks.HelpPage = {
    dbName() {
        return "sa-cloud"
    },
    storeName() {
        return "help"
    },
    handleKey() {
        return "local-content"
    },
    expandAnchor() {
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        const el = document.getElementById(hash);
        if (!el) return;
        const details = el.tagName === "DETAILS" ? el : el.closest("details");
        if (details) details.open = true;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    async mounted() {
        this.expandAnchor();
        window.addEventListener("phx:page-loading-stop", () => this.expandAnchor());
        window.addEventListener("hashchange", () => this.expandAnchor());
        this.el.addEventListener("click", event => {
            const button = event.target.closest(".product-card button[data-sku]");
            if (button) this.addToCart(button);
        });

        const editButton = this.el.querySelector("#edit-mode");
        if (!editButton) return;

        this.handleEvent("local-meta-loaded", async (e) => {
            let dirHandle = await this.getStoredHandle();
            await this.loadEditableContent(dirHandle);
        });

        editButton.addEventListener("click", async () => {
            await this.toggleEdit();
        })

        // Attempt auto-load on mount
        await this.tryAutoLoad();
    },

    // A server patch, such as picking a shop variant, re-renders the page from
    // the server's copy and so undoes the preview, toggle state included.
    updated() {
        const editButton = this.el.querySelector("#edit-mode");
        if (!editButton || editButton.classList.contains("active")) return;

        if (this.loading) this.reloadPending = true;
        else this.tryAutoLoad();
    },

    async toggleEdit() {
        const editButton = this.el.querySelector("#edit-mode");
        let dirHandle = await this.getStoredHandle();

        if (dirHandle) {
            const perm = await dirHandle.queryPermission({mode: "read"});
            if (perm !== "granted") {
                dirHandle = null;
            }
        }

        if (dirHandle) {
            await this.clearDirHandle();
            editButton.classList.remove("active");
            window.location.reload();
        } else {
            try {
                dirHandle = await window.showDirectoryPicker();
                const granted = await dirHandle.requestPermission({mode: "read"});
                if (granted !== "granted") {
                    alert("Permission denied.");
                    return;
                }
                await this.storeHandle(dirHandle);
            } catch (err) {
                console.error("Error selecting folder:", err);
                return;
            }
            await this.loadMetaData(dirHandle);
        }
    },

    parsePaths(multiple, single) {
        let paths = [];
        if (multiple) {
            try {
                paths = JSON.parse(multiple);
            } catch (e) {
                console.error("Invalid JSON in paths:", multiple);
            }
        }

        // Fallback to single path
        if (paths.length === 0 && single) {
            paths = [single];
        }
        return paths;
    },

    // Sends every sidecar found, in search order: a page that layers them,
    // such as a shop variant over its main product, needs them all.
    async loadMetaData(dirHandle) {
        this.loading = true;
        const paths = this.parsePaths(this.el.dataset.metaPaths, this.el.dataset.metaPath);
        const files = [];

        for (const path of paths) {
            try {
                const file = await this.readContentFile(dirHandle, path);
                files.push({path: path, content: await file.text()});
            } catch (err) {
            }
        }
        if (files.length > 0) {
            this.pushEvent("load-local-meta", {files: files});
        } else {
            await this.loadEditableContent(dirHandle);
        }
    },

    async loadEditableContent(dirHandle) {
        const editables = this.el.querySelectorAll(".editable");

        for (const el of editables) {
            const paths = this.parsePaths(el.dataset.contentPaths, el.dataset.contentPath);
            let resolved = null;

            for (const path of paths) {
                try {
                    const file = await this.readContentFile(dirHandle, path);
                    el.innerHTML = await file.text()
                    resolved = path;
                    await this.expandIncludes(dirHandle, el);
                    this.loadLocalImages(dirHandle, el);
                    await this.expandProducts(el);
                    break;
                } catch (err) {
                }
            }

            this.markLocal(el, dirHandle, paths, resolved);
        }

        this.loadServerImages(dirHandle);
        this.el.querySelector("#edit-mode").classList.add("active");
        this.loading = false;
        if (this.reloadPending) {
            this.reloadPending = false;
            this.tryAutoLoad();
        }
        updateTooltips();
        
        // Reinitialize Phoenix hooks for dynamically loaded content
        editables.forEach(el => {
            el.querySelectorAll('[phx-hook]').forEach(hookEl => {
                window.liveSocket.main.maybeAddNewHook(hookEl);
            });
        });
    },

    // Marks the load in flight before its first await, so a patch arriving
    // meanwhile queues a reload rather than starting a second one.
    async tryAutoLoad() {
        this.loading = true;
        const dirHandle = await this.getStoredHandle();

        if (dirHandle && await dirHandle.queryPermission({mode: "read"}) === "granted") {
            await this.loadMetaData(dirHandle);
        } else {
            this.loading = false;
        }
    },

    async openDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName(), 1);
            request.onupgradeneeded = () => {
                request.result.createObjectStore(this.storeName());
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error("IndexedDB failed to open:", request.error);
                reject(request.error);
            };
        });
    },

    async storeHandle(handle) {
        const db = await this.openDB();
        const tx = db.transaction(this.storeName(), "readwrite");
        tx.objectStore(this.storeName()).put(handle, this.handleKey());
        await tx.complete?.();
        db.close();
    },

    async getStoredHandle() {
        const db = await this.openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName(), "readonly");
            const store = tx.objectStore(this.storeName());
            const request = store.get(this.handleKey());
            request.onsuccess = () => {
                db.close();
                resolve(request.result);
            };
            request.onerror = () => {
                console.error("Error retrieving handle:", request.error);
                db.close();
                reject(request.error);
            };
        });
    },

    async clearDirHandle() {
        const db = await this.openDB();
        const tx = db.transaction(this.storeName(), "readwrite");
        tx.objectStore(this.storeName()).delete(this.handleKey());
        await tx.complete?.();
        db.close();
    },

    // Every path a page advertises is rooted at sa_help ("docs/…", "shop/…").
    // The picked folder is either that root, such as a worktree, or one of its
    // sections, whose own name then leads the path and is dropped.
    localPath(dirHandle, path) {
        const relativePath = path.replace(/^\//, "");
        const section = dirHandle.name + "/";
        return relativePath.startsWith(section) ? relativePath.slice(section.length) : relativePath;
    },

    async readFileFromDir(dirHandle, path) {
        const parts = this.localPath(dirHandle, path).split("/");
        let current = dirHandle;
        for (let i = 0; i < parts.length - 1; i++) {
            current = await current.getDirectoryHandle(parts[i]);
        }
        const fileHandle = await current.getFileHandle(parts.at(-1));
        return await fileHandle.getFile();
    },

    // Insert ".en" before the final extension (foo/bar.html -> foo/bar.en.html).
    enVariant(relativePath) {
        const dot = relativePath.lastIndexOf(".");
        if (dot === -1) return null;
        return relativePath.slice(0, dot) + ".en" + relativePath.slice(dot);
    },

    // Reads a content/meta file, mirroring the server's english_file/2
    // resolution: prefer the plain `<path>.<ext>`, then fall back to the
    // legacy `<path>.en.<ext>` form that most sa_help pages still use.
    async readContentFile(dirHandle, relativePath) {
        try {
            return await this.readFileFromDir(dirHandle, relativePath);
        } catch (err) {
            const enPath = this.enVariant(relativePath);
            if (!enPath) throw err;
            return await this.readFileFromDir(dirHandle, enPath);
        }
    },

    // Mirror the server's expand_include/2: replace each <sa-include src="…"/>
    // with the referenced fragment, wrapped in its own .editable region so it
    // gets the same dashed outline and "search order" bar as a page-level block.
    // The browser parser ignores the self-closing slash on this non-void element
    // and nests following siblings inside the node, so the wrapper is inserted
    // before the node and the node is then unwrapped (its children spliced back
    // in place) rather than assigning outerHTML, which would delete those
    // siblings. Non-recursive: the static NodeList is not re-scanned.
    //
    // A cross-section include blanks when a section folder was picked, since
    // the other section is outside it.
    async expandIncludes(dirHandle, element) {
        for (const node of element.querySelectorAll("sa-include")) {
            const src = node.getAttribute("src");
            const wrapper = document.createElement("div");
            let paths = [];
            let resolved = null;
            if (src) {
                const relativePath = src.replace(/^\//, "") + ".html";
                paths = [relativePath];
                try {
                    const file = await this.readContentFile(dirHandle, relativePath);
                    wrapper.innerHTML = await file.text();
                    resolved = relativePath;
                    this.loadLocalImages(dirHandle, wrapper);
                } catch (err) {
                }
            }
            this.markLocal(wrapper, dirHandle, paths, resolved);
            node.before(wrapper);
            node.replaceWith(...node.childNodes);
        }
    },

    async addToCart(button) {
        button.disabled = true;
        const response = await fetch("/shop/cart_items", {
            method: "POST",
            headers: {
                "content-type": "application/json",
                "x-csrf-token": document.querySelector("meta[name='csrf-token']").getAttribute("content"),
            },
            body: JSON.stringify({sku: button.dataset.sku}),
        });
        const result = await response.json().catch(() => ({}));

        if (response.ok) {
            const link = document.createElement("a");
            link.href = result.cart;
            link.className = "button";
            link.textContent = button.dataset.added;
            button.replaceWith(link);
        } else {
            if (result.error) button.textContent = result.error;
            button.disabled = false;
        }
    },

    // Mirror the server's expand_product/1 with the card it renders. Runs after
    // loadLocalImages, which would rewrite the card's already-resolved image src.
    // Unwrapped like <sa-include>, since the browser nests following siblings
    // inside a self-closed <sa-product/>.
    async expandProducts(element) {
        for (const node of element.querySelectorAll("sa-product")) {
            const card = document.createElement("div");
            const params = new URLSearchParams();
            const title = node.getAttribute("title");
            if (title) params.set("title", title);

            const response = await fetch(`/shop/product_card/${encodeURIComponent(node.getAttribute("sku") || "")}?${params}`);
            if (response.ok) {
                card.innerHTML = await response.text();
            } else {
                card.textContent = `⚠ <sa-product sku="${node.getAttribute("sku")}">: ${response.status}`;
            }

            node.before(...card.childNodes);
            node.replaceWith(...node.childNodes);
        }
    },

    // Add the dashed-outline .editable treatment and the hover "search order"
    // tooltip bar to a locally-sourced block — .override (bar shown inline) when
    // a file resolved, otherwise the bar only appears on hover.
    markLocal(el, dirHandle, searchPaths, resolvedPath) {
        el.querySelector(":scope > .editable-top-bar")?.remove();
        el.classList.remove("override");

        const tooltip = document.createElement("div");
        tooltip.id = `bar-${Math.random().toString(36).slice(6)}`;
        tooltip.innerHTML = `<h4>\u{1F4C1} ${dirHandle.name} search order:</h4>${searchPaths.join("<br/>")}`;
        tooltip.style.display = 'none';

        const bar = document.createElement("div");
        bar.classList.add("editable-top-bar");
        bar.setAttribute("data-html-tooltip", tooltip.id);
        bar.textContent = resolvedPath ? `\u{1F4C1} ${resolvedPath}` : `\u{1F4C1} Default`;

        el.classList.add("editable", "local");
        if (resolvedPath) el.classList.add("override");
        el.prepend(bar);
        bar.prepend(tooltip);
    },

    // Images in a local file carry the raw sa_help src. One missing from the
    // folder falls back to the copy the server serves.
    loadLocalImages(dirHandle, element) {
        element.querySelectorAll('img').forEach(async img => {
            const src = img.getAttribute('src');
            if (!src) return;

            if (src.startsWith('/docs/') || src.startsWith('/shop/')) {
                if (!await this.loadLocalImage(dirHandle, img, src)) {
                    console.warn(`Failed to load local image: ${src}`);
                    img.setAttribute('src', '/help-images' + src);
                }
            } else if (src.startsWith('/')) {
                img.setAttribute('src', '/images' + src);
            }
        });
    },

    // Server-rendered images that name their sa_help file in data-content-paths,
    // such as a shop product's photos. One missing from the folder is left as it is.
    loadServerImages(dirHandle) {
        this.el.querySelectorAll('img[data-content-paths]').forEach(img => {
            const [path] = this.parsePaths(img.dataset.contentPaths);
            if (path) this.loadLocalImage(dirHandle, img, path);
        });
    },

    async loadLocalImage(dirHandle, img, path) {
        try {
            const file = await this.readFileFromDir(dirHandle, path);
            const base64 = await this.blobToBase64(file);

            const ext = path.split('.').pop().toLowerCase();
            const mime = {
                png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
                svg: 'image/svg+xml', webp: 'image/webp', ico: 'image/x-icon', avif: 'image/avif'
            }[ext] || 'application/octet-stream';

            img.setAttribute('src', `data:${mime};base64, ${base64}`);
            return true;
        } catch (err) {
            return false;
        }
    },

    async blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
};

export {Hooks}